import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../lib/api";
import { recognizeRosterImage } from "../../lib/ocr";
import { preparationApi, type TournamentSetupPayload } from "../../lib/preparationApi";
import {
  STAFF_IMPORT_V1_EXAMPLE,
  STAFF_IMPORT_V1_FORMAT,
  normalizeStaffLogisticsImport,
} from "./staffImportV1";
import "./team-import.css";

type Mode = "" | "picker" | "text" | "housing";

function errorText(error: unknown) {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return String(error || "Nieznany błąd");
}

export function TeamImportToolbar({ tournamentId, data }: { tournamentId: string; data: TournamentSetupPayload }) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const ocrRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<Mode>("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [defaultRole, setDefaultRole] = useState("scorer");
  const [text, setText] = useState("");
  const [createMissing, setCreateMissing] = useState(false);
  const [ocrProgress, setOcrProgress] = useState("");
  const [housingText, setHousingText] = useState("");
  const [replaceRoster, setReplaceRoster] = useState(true);
  const [replaceLogistics, setReplaceLogistics] = useState(true);
  const [defaultTravel, setDefaultTravel] = useState(30);

  const employees = useQuery({
    queryKey: ["employees"],
    queryFn: preparationApi.employees,
    staleTime: 15_000,
  });
  const inRoster = useMemo(
    () => new Set(data.staff.map((person) => person.employeeId || person.personId)),
    [data.staff],
  );
  const available = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase("pl");
    return (employees.data?.employees || []).filter(
      (employee) =>
        employee.active &&
        !inRoster.has(employee.id) &&
        (!needle || `${employee.name} ${employee.aliases.join(" ")}`.toLocaleLowerCase("pl").includes(needle)),
    );
  }, [employees.data, inRoster, search]);

  const acceptSetup = (setup: TournamentSetupPayload) => {
    queryClient.setQueryData(["tournament-setup", tournamentId], setup);
    void queryClient.invalidateQueries({ queryKey: ["round-workspace", tournamentId] });
    void queryClient.invalidateQueries({ queryKey: ["generator-preflight", tournamentId] });
  };

  const add = useMutation({
    mutationFn: (ids: string[]) =>
      preparationApi.addStaff(tournamentId, {
        expectedRevision: data.configRevision,
        employeeIds: ids,
        role: defaultRole,
        baseId: data.accommodations[0]?.id || "",
      }),
    onSuccess: (result) => {
      acceptSetup(result.setup);
      setNotice(`Dodano ${result.added} osób do obsady.`);
      setSelected([]);
      setMode("");
    },
  });

  const clear = useMutation({
    mutationFn: () =>
      preparationApi.removeStaff(tournamentId, {
        expectedRevision: data.configRevision,
        personIds: data.staff.map((person) => person.personId),
      }),
    onSuccess: (result) => {
      acceptSetup(result.setup);
      setNotice(`Wyczyszczono obsadę: usunięto ${result.removed} osób.`);
    },
  });

  const importText = useMutation({
    mutationFn: () =>
      preparationApi.importStaffText(tournamentId, {
        expectedRevision: data.configRevision,
        text,
        role: defaultRole,
        createMissing,
      }),
    onSuccess: (result) => {
      acceptSetup(result.setup);
      setNotice(
        `Import listy: dodano ${result.added}, już było ${result.alreadyPresent}, utworzono w bazie ${result.createdEmployees}${result.missing.length ? `, brak dopasowania ${result.missing.length}` : ""}.`,
      );
      setText("");
      setMode("");
      void queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
  });

  const importHousing = useMutation({
    mutationFn: () =>
      preparationApi.importRosterLogistics(tournamentId, {
        expectedRevision: data.configRevision,
        text: normalizeStaffLogisticsImport(housingText),
        createMissing,
        replaceRoster,
        replaceLogistics,
        defaultTravelMinutes: defaultTravel,
      }),
    onSuccess: (result) => {
      acceptSetup(result.setup);
      const counts = result.counts || {};
      setNotice(
        `Import DOM/AUTO: ${counts.accommodations || 0} noclegów, ${counts.vehicles || 0} aut, ${counts.people || 0} osób, ${counts.drivers || 0} kierowców${result.missing.length ? ` · brak dopasowania ${result.missing.length}` : ""}.`,
      );
      setHousingText("");
      setMode("");
      void queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
  });

  const importXlsx = useMutation({
    mutationFn: (file: File) =>
      preparationApi.importStaffXlsx(tournamentId, file, {
        expectedRevision: data.configRevision,
        role: defaultRole,
        createMissing,
      }),
    onSuccess: (result) => {
      acceptSetup(result.setup);
      setNotice(
        `Excel: dodano ${result.added}, utworzono ${result.createdEmployees}, już było ${result.alreadyPresent}${result.missing.length ? `, brak dopasowania ${result.missing.length}` : ""}. Zielone oznaczenia kierowców zostały odczytane.`,
      );
      void queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
  });

  const addAllActive = () => {
    const ids = (employees.data?.employees || [])
      .filter((employee) => employee.active && !inRoster.has(employee.id))
      .map((employee) => employee.id);
    if (!ids.length) return setNotice("Wszyscy aktywni pracownicy są już w obsadzie.");
    add.mutate(ids);
  };

  async function ocr(file?: File) {
    if (!file) return;
    setMode("text");
    setOcrProgress("OCR start…");
    try {
      const result = await recognizeRosterImage(file, ({ status, progress }) =>
        setOcrProgress(`${status} · ${Math.round(progress * 100)}%`),
      );
      setText(result);
      setOcrProgress(`OCR gotowy · ${result.split(/\n/).filter(Boolean).length} wierszy. Sprawdź tekst przed importem.`);
    } catch (error) {
      setOcrProgress(errorText(error));
    }
  }

  async function copyStaffFormat() {
    try {
      await navigator.clipboard.writeText(STAFF_IMPORT_V1_EXAMPLE);
      setNotice(`Skopiowano przykład ${STAFF_IMPORT_V1_FORMAT}. Możesz wkleić go do ChatGPT albo edytować i zaimportować tutaj.`);
    } catch {
      setHousingText(STAFF_IMPORT_V1_EXAMPLE);
      setNotice("Nie udało się użyć schowka — przykład został wstawiony do pola importu.");
    }
  }

  const errors = [employees.error, add.error, clear.error, importText.error, importHousing.error, importXlsx.error].filter(Boolean);

  return (
    <article className="panel team-import-card">
      <header>
        <div>
          <span className="eyebrow">SZYBKIE PRZYGOTOWANIE</span>
          <h3>Dodaj lub importuj obsadę</h3>
          <p>Baza, tekst, screen OCR, Excel i pełny DOM/AUTO/ROLA są w jednym miejscu obsady.</p>
        </div>
        <span className="team-import-pool">Baza: {employees.data?.count || 0}</span>
      </header>

      <div className="team-import-actions">
        <button type="button" className="primary-button" onClick={() => setMode(mode === "picker" ? "" : "picker")}>+ Dodaj z bazy</button>
        <button type="button" className="secondary-button" onClick={() => setMode(mode === "text" ? "" : "text")}>Wklej listę</button>
        <button type="button" className="secondary-button" onClick={() => ocrRef.current?.click()}>OCR screena</button>
        <input ref={ocrRef} hidden type="file" accept="image/*" onChange={(event) => { void ocr(event.target.files?.[0]); event.currentTarget.value = ""; }} />
        <button type="button" className="secondary-button" onClick={() => setMode(mode === "housing" ? "" : "housing")}>DOM / AUTO / ROLA</button>
        <button type="button" className="secondary-button" disabled={add.isPending} onClick={addAllActive}>Dodaj wszystkich aktywnych</button>
        <button type="button" className="secondary-button" onClick={() => fileRef.current?.click()}>Import Excel + kolory</button>
        <input ref={fileRef} hidden type="file" accept=".xlsx,.xlsm" onChange={(event) => { const file = event.target.files?.[0]; if (file) importXlsx.mutate(file); event.currentTarget.value = ""; }} />
        <button type="button" className="secondary-button danger" disabled={!data.staff.length || clear.isPending} onClick={() => { if (window.confirm(`Wyczyścić całą obsadę (${data.staff.length} osób)?`)) clear.mutate(); }}>Wyczyść skład</button>
      </div>

      {notice ? <div className="team-import-notice">{notice}</div> : null}
      {errors.length ? <div className="team-import-notice error">{errorText(errors[0])}</div> : null}

      {mode === "picker" ? (
        <section className="team-import-panel">
          <div className="team-import-panel-head">
            <label><span>Szukaj w bazie</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Nazwisko…" /></label>
            <label><span>Rola R1–R4</span><select value={defaultRole} onChange={(event) => setDefaultRole(event.target.value)}><option value="scorer">Scoring</option><option value="setup">Setup</option><option value="dm">Data Manager / Control</option><option value="off">Poza rundą</option></select></label>
          </div>
          <div className="team-picker-list">{available.map((employee) => <label key={employee.id}><input type="checkbox" checked={selected.includes(employee.id)} onChange={() => setSelected((current) => current.includes(employee.id) ? current.filter((id) => id !== employee.id) : [...current, employee.id])} /><strong>{employee.name}</strong>{employee.canDriveGenerally ? <small>K</small> : null}</label>)}</div>
          {!available.length ? <p className="hint">Brak kolejnych aktywnych osób pasujących do wyszukiwania.</p> : null}
          <footer><button type="button" className="secondary-button" onClick={() => setSelected(available.map((row) => row.id))}>Zaznacz widocznych</button><button type="button" className="primary-button" disabled={!selected.length || add.isPending} onClick={() => add.mutate(selected)}>Dodaj zaznaczonych ({selected.length})</button></footer>
        </section>
      ) : null}

      {mode === "text" ? (
        <section className="team-import-panel">
          {ocrProgress ? <div className="team-import-notice">{ocrProgress}</div> : null}
          <textarea rows={9} value={text} onChange={(event) => setText(event.target.value)} placeholder={'1 Piotr Łastawiecki\n2 Paweł Ślusarczyk\n3 Maciej Pędowski'} />
          <div className="team-import-options">
            <label><span>Rola R1–R4</span><select value={defaultRole} onChange={(event) => setDefaultRole(event.target.value)}><option value="scorer">Scoring</option><option value="setup">Setup</option><option value="dm">Data Manager / Control</option><option value="off">Poza rundą</option></select></label>
            <label className="check-line"><input type="checkbox" checked={createMissing} onChange={(event) => setCreateMissing(event.target.checked)} /> Utwórz brakujące osoby w bazie</label>
            <button type="button" className="primary-button" disabled={!text.trim() || importText.isPending} onClick={() => importText.mutate()}>{importText.isPending ? "Importuję…" : "Dodaj listę do składu"}</button>
          </div>
        </section>
      ) : null}

      {mode === "housing" ? (
        <section className="team-import-panel housing">
          <p className="hint">
            Zalecany format: <strong>{STAFF_IMPORT_V1_FORMAT}</strong>. DOM, SCORING ROLA, AUTO, OSOBY i <strong>[K]</strong> są czytelne także po wklejeniu do ChatGPT. <strong>AUTA WSPÓLNE</strong> dotyczą aktualnego DOM (np. całego hotelu). Jeżeli dwa Airbnb korzystają z jednego auta, wpisz je jako jedną bazę transportową, np. <strong>DOM: Airbnb 7 + Airbnb 8</strong>.
          </p>
          <div className="team-import-actions">
            <button type="button" className="secondary-button" onClick={() => setHousingText(STAFF_IMPORT_V1_EXAMPLE)}>Wstaw przykład</button>
            <button type="button" className="secondary-button" onClick={() => { void copyStaffFormat(); }}>Kopiuj przykład</button>
          </div>
          <textarea
            rows={18}
            value={housingText}
            onChange={(event) => setHousingText(event.target.value)}
            placeholder={STAFF_IMPORT_V1_EXAMPLE}
          />
          <div className="team-import-options housing-options">
            <label><span>Domyślny dojazd</span><input type="number" min={0} max={240} value={defaultTravel} onChange={(event) => setDefaultTravel(Number(event.target.value))} /></label>
            <label className="check-line"><input type="checkbox" checked={createMissing} onChange={(event) => setCreateMissing(event.target.checked)} /> Utwórz brakujące osoby</label>
            <label className="check-line"><input type="checkbox" checked={replaceRoster} onChange={(event) => setReplaceRoster(event.target.checked)} /> Zastąp obecną obsadę</label>
            <label className="check-line"><input type="checkbox" checked={replaceLogistics} onChange={(event) => setReplaceLogistics(event.target.checked)} /> Zastąp noclegi i auta</label>
            <button type="button" className="primary-button" disabled={!housingText.trim() || importHousing.isPending} onClick={() => importHousing.mutate()}>{importHousing.isPending ? "Stosuję…" : "Zastosuj i zapisz"}</button>
          </div>
        </section>
      ) : null}
    </article>
  );
}
